import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '@/lib/api-client';
import { getMockPublicSession } from '@/lib/mock-public-session';
import { CheckoutShell } from './CheckoutShell';
import { StatusChip } from './StatusChip';
import { RailTab } from './RailTab';
import { WifiOff } from 'lucide-react';

interface PublicSession {
  sessionId: string;
  tenantName?: string;
  amountUsdCents: number;
  accepts: string;
  status: string;
  expiresAt: number;
  successRedirectUrl?: string;
}

export const ExpiredPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [session, setSession] = useState<PublicSession | null>(null);

  useEffect(() => {
    if (!sessionId) return;
    api.get(`/api/v1/sessions/${sessionId}/public`)
      .then(setSession)
      .catch(async (error) => {
        const fallback = await getMockPublicSession(sessionId);
        if (fallback) {
          setSession(fallback);
          return;
        }
        console.error(error);
      });
  }, [sessionId]);

  const usdStr = session ? (session.amountUsdCents / 100).toFixed(2) : '—';
  const expiredTime = session
    ? new Date(session.expiresAt * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZoneName: 'short' })
    : null;
  const rails = session?.accepts?.split(',') ?? [];

  return (
    <CheckoutShell>
      <div className='flex-1 flex items-start justify-center p-4 md:p-8'>
        <div className='w-full max-w-3xl flex flex-col md:flex-row gap-5'>

          {/* Left: info card */}
          <div
            className='flex flex-col gap-4 p-6 rounded-xl border flex-shrink-0 md:w-72'
            style={{ background: 'var(--pp-surface)', borderColor: 'var(--pp-border)' }}
          >
            <StatusChip status='expired' />

            <div
              className='text-3xl font-semibold tabular-nums'
              style={{ fontFamily: 'var(--pp-mono)', color: 'var(--pp-text)', letterSpacing: '-0.03em' }}
            >
              ${usdStr}
            </div>

            <p className='text-sm leading-relaxed' style={{ color: 'var(--pp-text-muted)' }}>
              Request a new link from the app to complete this payment.
            </p>

            <div
              className='text-xs border-t pt-3 flex flex-col gap-2'
              style={{ borderColor: 'var(--pp-border)', color: 'var(--pp-text-subtle)' }}
            >
              {expiredTime && (
                <div className='flex justify-between'>
                  <span className='uppercase tracking-wide text-[10px] font-medium'>Expired</span>
                  <span style={{ fontFamily: 'var(--pp-mono)' }}>{expiredTime}</span>
                </div>
              )}
              {session && (
                <div className='flex justify-between'>
                  <span className='uppercase tracking-wide text-[10px] font-medium'>Ref</span>
                  <span style={{ fontFamily: 'var(--pp-mono)' }}>{session.sessionId}</span>
                </div>
              )}
            </div>

            <div className='flex flex-col gap-2 mt-1'>
              {session?.successRedirectUrl ? (
                <a
                  href={session.successRedirectUrl}
                  className='w-full py-2.5 px-4 rounded text-sm font-semibold text-center text-white'
                  style={{ background: 'var(--pp-action)' }}
                >
                  Request new link
                </a>
              ) : (
                <button
                  disabled
                  className='w-full py-2.5 px-4 rounded text-sm font-semibold text-center cursor-not-allowed opacity-40'
                  style={{ background: 'var(--pp-action)', color: '#fff' }}
                >
                  Request new link
                </button>
              )}
              {session?.successRedirectUrl && (
                <a
                  href={session.successRedirectUrl}
                  className='w-full py-2 text-sm text-center border rounded transition-colors hover:bg-gray-50'
                  style={{ borderColor: 'var(--pp-border)', color: 'var(--pp-text-muted)' }}
                >
                  Back to app
                </a>
              )}
            </div>
          </div>

          {/* Right: disabled rail preview */}
          <div
            className='flex-1 flex flex-col gap-4 p-6 rounded-xl border'
            style={{ background: 'var(--pp-surface)', borderColor: 'var(--pp-border)', opacity: 0.55 }}
          >
            {rails.length > 0 && (
              <div className='flex gap-2'>
                {rails.map((rail) => (
                  <RailTab key={rail} rail={rail} active={false} disabled />
                ))}
              </div>
            )}

            <div className='flex-1 flex flex-col items-center justify-center gap-3 py-8'>
              <div
                className='w-12 h-12 rounded-full flex items-center justify-center'
                style={{ background: 'var(--pp-subtle)' }}
              >
                <WifiOff size={20} style={{ color: 'var(--pp-text-subtle)' }} />
              </div>
              <p className='text-sm text-center max-w-xs' style={{ color: 'var(--pp-text-muted)' }}>
                Payment actions are unavailable for expired sessions.
              </p>
            </div>
          </div>
        </div>
      </div>
    </CheckoutShell>
  );
};

export default ExpiredPage;
